/**
 * Auth Service
 * Handles authentication operations via Supabase
 */

import { supabase } from '@/integrations/supabase/client';
import { getCachedUser, getCachedSession } from '@/contexts/AuthContext';

export interface AuthUser {
  id: string;
  email?: string;
}

export interface AuthResult {
  user: AuthUser | null;
  error: Error | null;
  session?: { access_token: string; refresh_token: string } | null;
}

export const authService = {
  /**
   * Sign in with email/password
   */
  async signIn(email: string, password: string): Promise<AuthResult> {
    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) {
        return { user: null, error };
      }

      return {
        user: data.user ? { id: data.user.id, email: data.user.email ?? undefined } : null,
        error: null,
      };
    } catch (e) {
      return { user: null, error: e instanceof Error ? e : new Error('Sign in failed') };
    }
  },

  /**
   * Sign up with email/password
   */
  async signUp(
    email: string,
    password: string,
    options?: { role?: 'adopter' | 'curator' }
  ): Promise<AuthResult> {
    try {
      // IMPORTANT: redirect into our SPA callback handler to finalize auth and route user correctly
      const redirectUrl = `${window.location.origin}/auth/callback`;

      const role = options?.role;

      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          emailRedirectTo: redirectUrl,
          ...(role ? { data: { role } } : {}),
        },
      });

      if (error) {
        return { user: null, error, session: null };
      }

      return {
        user: data.user ? { id: data.user.id, email: data.user.email ?? undefined } : null,
        error: null,
        session: data.session ? { 
          access_token: data.session.access_token, 
          refresh_token: data.session.refresh_token 
        } : null,
      };
    } catch (e) {
      return { user: null, error: e instanceof Error ? e : new Error('Sign up failed'), session: null };
    }
  },

  /**
   * Sign out current user
   */
  async signOut(): Promise<{ error: Error | null }> {
    try {
      const { error } = await supabase.auth.signOut();
      return { error };
    } catch (e) {
      return { error: e instanceof Error ? e : new Error('Sign out failed') };
    }
  },

  /**
   * Get current authenticated user (if any)
   * Uses cached data from AuthContext for synchronous access
   */
  getCurrentUser(): AuthUser | null {
    const cachedUser = getCachedUser();
    if (!cachedUser) return null;
    return { id: cachedUser.id, email: cachedUser.email ?? undefined };
  },

  /**
   * Get current session from cache
   * Uses cached data from AuthContext for synchronous access
   */
  refreshSession(): AuthResult {
    const session = getCachedSession();
    
    if (!session?.user) {
      return { user: null, error: null };
    }

    return {
      user: { id: session.user.id, email: session.user.email ?? undefined },
      error: null,
    };
  },

  /**
   * Check if there's an existing session (synchronous, uses cache)
   */
  hasSession(): boolean {
    return !!getCachedSession();
  },

  /**
   * Check if email is already in use
   */
  async isEmailInUse(email: string): Promise<{ inUse: boolean; error: Error | null }> {
    try {
      // Use SECURITY DEFINER RPC to allow anonymous users to check email existence
      const { data, error } = await supabase
        .rpc('check_email_exists', { p_email: email });

      if (error) {
        return { inUse: false, error };
      }

      return { inUse: !!data, error: null };
    } catch (e) {
      return { inUse: false, error: e instanceof Error ? e : new Error('Email check failed') };
    }
  },

  /**
   * Update user email
   * Sends confirmation to both old and new email addresses
   */
  async updateEmail(newEmail: string): Promise<{ error: Error | null }> {
    try {
      const redirectUrl = `${window.location.origin}/profile`;

      const { error } = await supabase.auth.updateUser({
        email: newEmail,
      }, {
        emailRedirectTo: redirectUrl,
      });

      if (error) {
        return { error };
      }

      return { error: null };
    } catch (e) {
      return { error: e instanceof Error ? e : new Error('Email update failed') };
    }
  },

  /**
   * Verify email with OTP code
   * Used for email confirmation after signup
   */
  async verifyEmailOtp(email: string, token: string): Promise<AuthResult> {
    try {
      const { data, error } = await supabase.auth.verifyOtp({
        email,
        token,
        type: 'signup',
      });

      if (error) {
        return { user: null, error, session: null };
      }

      return {
        user: data.user ? { id: data.user.id, email: data.user.email ?? undefined } : null,
        error: null,
        session: data.session ? { 
          access_token: data.session.access_token, 
          refresh_token: data.session.refresh_token 
        } : null,
      };
    } catch (e) {
      return { user: null, error: e instanceof Error ? e : new Error('OTP verification failed'), session: null };
    }
  },
};

export default authService;
